import { Injectable, signal, computed } from '@angular/core';
import { SalesStats } from 'src/app/models/core/chart-interfaces';
import { formatCurrency } from 'src/app/utils/currency-format';
import { GlobalReportApiService } from './global-report-api.service';
import { HeaderKpisService } from './header-kpis.service';
import { sellerStat } from 'src/app/models/core/sales-manager/seller.model';
import { IsLatestRequest, latestRequestTracker } from 'src/app/utils/reports/latest-request';

/**
 * El ranking de vendedores se muestra completo (sin scroll infinito): se piden
 * todos los vendedores del evento en una sola request. El backend pagina con
 * `paginate(per_page)` sin tope, así que un per_page holgado trae todo de una.
 * Domino máximo observado ~300 vendedores/evento; 1000 deja margen 3x.
 */
const ALL_SELLERS_PER_PAGE = 1000;

export interface SellerReportItem {
  id: number;
  name: string;
  email: string;
  avatar?: string;
  total_amount: number;
  total_amount_formatted?: string;
  total_tickets: number;
  total_assistants: number;
  verified_assistants: number;
  verified_percentage: number;
  absent_assistants: number;
  absent_percentage: number;
}

@Injectable({ providedIn: 'root' })
export class SellerReportService {
  readonly sellersLoading = signal(false);
  readonly sellersStats = signal<sellerStat[]>([]);

  readonly currentPage = signal(1);
  readonly lastPage = signal(1);
  readonly total = signal(0);
  readonly showLoadMore = signal(false);
  readonly isLoadingMore = signal(false);

  readonly selectedSellerLoading = signal(false);
  readonly selectedSeller = signal<SellerReportItem | null>(null);
  readonly selectedSellerStats = signal<SalesStats[]>([]);
  readonly selectedSellerTotalFormatted = computed(() =>
    this.selectedSeller()
      ? formatCurrency(this.selectedSeller()!.total_amount)
      : '$0',
  );

  readonly sellersSortOrder = signal<'most_sales' | 'least_sales'>('most_sales');

  /** Opciones de orden; `labelKey` se traduce donde se muestra. */
  readonly sellersSortOptions = [
    { labelKey: 'sales-breakdown.sort.most-sold', value: 'most_sales' },
    { labelKey: 'sales-breakdown.sort.least-sold', value: 'least_sales' },
  ];

  readonly searchQuery = signal('');

  private readonly initialRequest = latestRequestTracker();
  /** Lista vigente: una recarga (rol, orden, búsqueda) la invalida junto con sus "Cargar más". */
  private isCurrentList: IsLatestRequest = this.initialRequest.start();

  constructor(
    private globalReportApi: GlobalReportApiService,
    private headerKpisService: HeaderKpisService,
  ) {}

  loadInitialSellers(): void {
    const eventId = this.headerKpisService.eventId;
    if (!eventId) return;

    this.sellersLoading.set(true);
    // Al cambiar de rol se pide de nuevo: la respuesta del rol anterior no pisa la nueva.
    const isLatest = this.initialRequest.start();
    this.isCurrentList = isLatest;
    this.isLoadingMore.set(false);
    this.currentPage.set(1);
    this.lastPage.set(1);
    this.total.set(0);
    this.showLoadMore.set(false);
    this.sellersStats.set([]);

    this.globalReportApi
      .getSellers(eventId, this.sellersSortOrder(), this.searchQuery(), 1, ALL_SELLERS_PER_PAGE)
      .subscribe({
        next: ({ data = [], meta = {} }) => {
          if (!isLatest()) return;
          this.updateSellersList(data, meta, true);
        },
        error: () => {
          if (!isLatest()) return;
          this.sellersLoading.set(false);
        },
        complete: () => {
          if (!isLatest()) return;
          this.sellersLoading.set(false);
        },
      });
  }

  loadMoreSellers(): void {
    const nextPage = this.currentPage() + 1;
    if (nextPage > this.lastPage()) {
      this.showLoadMore.set(false);
      return;
    }

    const eventId = this.headerKpisService.eventId;
    if (!eventId) return;

    this.isLoadingMore.set(true);
    const isLatest = this.isCurrentList;

    this.globalReportApi
      .getSellers(eventId, this.sellersSortOrder(), this.searchQuery(), nextPage, 10)
      .subscribe({
        next: ({ data = [], meta = {} }) => {
          if (!isLatest()) return;
          this.updateSellersList(data, meta, false);
        },
        error: () => {
          if (!isLatest()) return;
          this.isLoadingMore.set(false);
        },
        complete: () => {
          if (!isLatest()) return;
          this.isLoadingMore.set(false);
        },
      });
  }

  changeSortOrder(order: 'most_sales' | 'least_sales'): void {
    if (this.sellersSortOrder() === order) return;
    this.sellersSortOrder.set(order);
    this.loadInitialSellers();
  }

  searchSellers(query: string): void {
    this.searchQuery.set(query);
    this.loadInitialSellers();
  }

  getSelectedSortLabelKey(): string {
    return this.sellersSortOptions.find(
      opt => opt.value === this.sellersSortOrder()
    )?.labelKey || '';
  }

  private updateSellersList(
    newSellers: sellerStat[],
    meta: { current_page?: number; last_page?: number; total?: number },
    replace: boolean,
  ): void {
    if (replace) {
      this.sellersStats.set(newSellers);
    } else {
      const currentIds = new Set(this.sellersStats().map(s => s.seller_id));
      const merged = [
        ...this.sellersStats(),
        ...newSellers.filter(s => !currentIds.has(s.seller_id)),
      ];
      this.sellersStats.set(merged);
    }

    this.currentPage.set(meta.current_page ?? 1);
    this.lastPage.set(meta.last_page ?? 1);
    if (meta.total !== undefined) {
      this.total.set(meta.total);
    }
    this.showLoadMore.set((meta.current_page ?? 1) < (meta.last_page ?? 1));
  }

  resetAllState(): void {
    this.sellersStats.set([]);
    this.sellersLoading.set(false);
    this.currentPage.set(1);
    this.lastPage.set(1);
    this.total.set(0);
    this.showLoadMore.set(false);
    this.isLoadingMore.set(false);
    this.selectedSeller.set(null);
    this.selectedSellerStats.set([]);
    this.selectedSellerLoading.set(false);
    this.searchQuery.set('');
  }
}
